/**
 * Public FormMaker file dropzone — uploads immediately via XHR into session tmp,
 * form submit only sends tokens. Without JS, native multipart still works.
 */

interface QueuedFile {
    id: number;
    file: File;
    token: string | null;
    status: 'queued' | 'uploading' | 'done' | 'error';
    percent: number;
    xhr: XMLHttpRequest | null;
    knownSize?: number;
}

function applyI18nTemplate(template: string, vars: Record<string, string>): string {
    let out = template;
    Object.keys(vars).forEach((key) => {
        out = out.split('{{' + key + '}}').join(vars[key]);
    });
    return out;
}

function parseJsonObject(raw: string): { ok?: boolean; token?: string; error?: string } | null {
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start < 0 || end <= start) {
        return null;
    }
    try {
        return JSON.parse(raw.slice(start, end + 1)) as { ok?: boolean; token?: string; error?: string };
    } catch {
        return null;
    }
}

function zoneI18n(el: Element | null, key: string, vars: Record<string, string> = {}): string {
    const fallbacks: Record<string, string> = {
        uploading: 'Nahrávám soubory…',
        'uploading-pct': 'Nahrávám soubory… {{percent}} %',
        'remove-file': 'Odstranit soubor {{name}}',
        'max-files': 'Lze nahrát nejvýše {{max}} souborů.',
        'min-files': 'Nahrajte alespoň {{min}} souborů.',
        'bad-type': 'Tyto soubory mají nepovolený typ: {{files}}.',
        'too-large': 'Tyto soubory překračují maximální velikost: {{files}}.',
        'upload-failed': 'Soubory se nepodařilo nahrát. Zkontrolujte připojení a zkuste to znovu.',
    };
    let raw = '';
    if (el instanceof HTMLElement) {
        raw = el.getAttribute('data-i18n-' + key) || '';
    }
    if (raw === '') {
        raw = fallbacks[key] || '';
    }
    return applyI18nTemplate(raw, vars);
}

function fileListToArray(list: FileList | null): Array<File> {
    if (!list) {
        return [];
    }
    return Array.from(list);
}

function fileKey(file: File): string {
    return file.name + '|' + String(file.size) + '|' + String(file.lastModified);
}

function fileExtension(file: File): string {
    const name = file.name;
    const dot = name.lastIndexOf('.');
    if (dot <= 0 || dot >= name.length - 1) {
        return '';
    }
    return name.slice(dot + 1).toLowerCase();
}

function parseAllowedExtensions(accept: string): Array<string> {
    return accept
        .split(',')
        .map((part) => part.trim().toLowerCase().replace(/^\./, ''))
        .filter((ext) => ext !== '');
}

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return String(bytes) + ' B';
    }
    if (bytes < 1024 * 1024) {
        const kb = bytes / 1024;
        return (kb >= 10 ? String(Math.round(kb)) : kb.toFixed(1).replace('.', ',')) + ' kB';
    }
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(mb >= 10 ? 0 : 1).replace('.', ',') + ' MB';
}

function fileTypeLabel(file: File): string {
    const ext = fileExtension(file);
    if (ext !== '') {
        return ext.toUpperCase();
    }
    return file.type || '';
}

function setInputFiles(input: HTMLInputElement, files: Array<File>): void {
    const transfer = new DataTransfer();
    files.forEach((file) => {
        transfer.items.add(file);
    });
    input.files = transfer.files;
}

function ensureFeedback(zone: HTMLElement): HTMLElement {
    const existing = zone.querySelector('.js-formmaker-dropzone-feedback');
    if (existing instanceof HTMLElement) {
        return existing;
    }
    const feedback = document.createElement('span');
    feedback.className = 'FormMaker__dropzone-feedback js-formmaker-dropzone-feedback';
    feedback.setAttribute('role', 'alert');
    zone.appendChild(feedback);
    return feedback;
}

function showFeedback(feedback: HTMLElement, messages: Array<string>): void {
    feedback.innerHTML = '';
    feedback.hidden = messages.length === 0;
    messages.forEach((text) => {
        const line = document.createElement('span');
        line.className = 'FormMaker__dropzone-feedback-item error';
        line.textContent = text;
        feedback.appendChild(line);
    });
}

interface IncomingResult {
    files: Array<File>;
    messages: Array<string>;
}

function collectIncoming(
    existing: Array<File>,
    incoming: Array<File>,
    multiple: boolean,
    maxFiles: number,
    allowed: Array<string>,
    maxBytes: number,
    zone: HTMLElement,
): IncomingResult {
    const messages: Array<string> = [];
    const rejectedType: Array<string> = [];
    const rejectedSize: Array<string> = [];
    let overLimit = false;
    let next = multiple ? existing.slice() : [];
    const seen = new Set(next.map(fileKey));

    incoming.forEach((file) => {
        const ext = fileExtension(file);
        if (allowed.length > 0 && (ext === '' || !allowed.includes(ext))) {
            rejectedType.push(file.name);
            return;
        }
        if (maxBytes > 0 && file.size > maxBytes) {
            rejectedSize.push(file.name);
            return;
        }
        if (!multiple) {
            next = [file];
            return;
        }
        const key = fileKey(file);
        if (seen.has(key)) {
            return;
        }
        if (next.length >= maxFiles) {
            overLimit = true;
            return;
        }
        seen.add(key);
        next.push(file);
    });

    if (overLimit) {
        messages.push(zoneI18n(zone, 'max-files', { max: String(maxFiles) }));
    }
    if (rejectedType.length > 0) {
        messages.push(zoneI18n(zone, 'bad-type', { files: rejectedType.join(', ') }));
    }
    if (rejectedSize.length > 0) {
        messages.push(zoneI18n(zone, 'too-large', { files: rejectedSize.join(', ') }));
    }

    return { files: next, messages };
}

function setZoneProgress(zone: HTMLElement, percent: number | null, indeterminate: boolean): void {
    zone.classList.toggle('is-uploading', percent !== null);
    const node = zone.querySelector('.js-formmaker-upload-progress');
    if (!(node instanceof HTMLElement)) {
        return;
    }
    if (percent === null) {
        node.hidden = true;
        node.classList.remove('is-indeterminate');
        return;
    }
    node.hidden = false;
    node.classList.toggle('is-indeterminate', indeterminate);
    const bar = node.querySelector('.FormMaker__upload-progress-bar');
    if (bar instanceof HTMLElement) {
        bar.style.width = indeterminate ? '' : String(Math.max(0, Math.min(100, percent))) + '%';
    }
    const label = node.querySelector('.js-formmaker-upload-progress-label');
    if (label instanceof HTMLElement) {
        if (indeterminate) {
            label.textContent = zoneI18n(zone, 'uploading');
        } else {
            label.textContent = zoneI18n(zone, 'uploading-pct', { percent: String(Math.round(percent)) });
        }
    }
}

let nextQueueId = 1;

function initImmediateZone(zone: HTMLElement, input: HTMLInputElement, queue: HTMLElement): void {
    const multiple = zone.getAttribute('data-multiple') === '1';
    const maxFiles = Math.max(1, Number(zone.getAttribute('data-max-files') || '1'));
    const maxBytes = Math.max(0, Number(zone.getAttribute('data-max-size-kb') || '0')) * 1024;
    const allowed = parseAllowedExtensions(input.getAttribute('accept') || '');
    const uploadUrl = zone.getAttribute('data-upload-url') || '';
    const formId = zone.getAttribute('data-form-id') || '';
    const itemId = zone.getAttribute('data-item-id') || '';
    const relationId = zone.getAttribute('data-relation-id') || '';
    const tokensName = zone.getAttribute('data-tokens-name') || '';
    const feedback = ensureFeedback(zone);
    const existingTokensHost = zone.querySelector('.js-formmaker-tokens');
    let tokensHost: HTMLElement;
    if (existingTokensHost instanceof HTMLElement) {
        tokensHost = existingTokensHost;
    } else {
        tokensHost = document.createElement('span');
        tokensHost.className = 'js-formmaker-tokens';
        tokensHost.hidden = true;
        zone.appendChild(tokensHost);
    }
    input.removeAttribute('name');

    const hydrateRestored = (): Array<QueuedFile> => {
        const restored: Array<QueuedFile> = [];
        tokensHost.querySelectorAll('input[type="hidden"]').forEach((node) => {
            if (!(node instanceof HTMLInputElement)) {
                return;
            }
            const token = node.value.trim().toLowerCase();
            if (!/^[a-f0-9]{32}$/.test(token)) {
                return;
            }
            const name = node.getAttribute('data-name') || token;
            const size = Math.max(0, Number(node.getAttribute('data-size') || '0'));
            const mime = node.getAttribute('data-mime') || 'application/octet-stream';
            restored.push({
                id: nextQueueId++,
                file: new File([], name, { type: mime }),
                token,
                status: 'done',
                percent: 100,
                xhr: null,
                knownSize: size,
            });
        });
        return restored;
    };

    let items: Array<QueuedFile> = hydrateRestored();

    const syncTokens = (): void => {
        tokensHost.innerHTML = '';
        items.forEach((item) => {
            if (!item.token) {
                return;
            }
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = tokensName;
            hidden.value = item.token;
            hidden.setAttribute('data-name', item.file.name);
            hidden.setAttribute('data-size', String(item.knownSize ?? item.file.size));
            tokensHost.appendChild(hidden);
        });
    };

    const overallPercent = (): number | null => {
        const active = items.filter((item) => item.status === 'uploading' || item.status === 'queued');
        if (active.length === 0) {
            return null;
        }
        const uploading = items.filter((item) => item.status === 'uploading');
        if (uploading.length === 0) {
            return 0;
        }
        const sum = uploading.reduce((acc, item) => acc + item.percent, 0);
        return sum / uploading.length;
    };

    const render = (): void => {
        queue.innerHTML = '';
        items.forEach((item, index) => {
            const li = document.createElement('li');
            li.className = 'FormMaker__dropzone-file';
            if (item.status === 'uploading' || item.status === 'queued') {
                li.classList.add('is-uploading');
            }
            if (item.status === 'error') {
                li.classList.add('is-error');
            }

            const info = document.createElement('span');
            info.className = 'FormMaker__dropzone-file-info';

            const name = document.createElement('span');
            name.className = 'FormMaker__dropzone-file-name';
            name.textContent = item.file.name;

            const meta = document.createElement('span');
            meta.className = 'FormMaker__dropzone-file-meta';
            const typeLabel = fileTypeLabel(item.file);
            const sizeLabel = formatSize(item.knownSize ?? item.file.size);
            meta.textContent = typeLabel !== '' ? sizeLabel + ' · ' + typeLabel : sizeLabel;

            info.appendChild(name);
            info.appendChild(meta);

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'FormMaker__dropzone-file-remove';
            remove.setAttribute('aria-label', zoneI18n(zone, 'remove-file', { name: item.file.name }));
            remove.innerHTML =
                '<svg class="ico ico--close" xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
                '<path d="M0 0h24v24H0V0z" fill="none"/>' +
                '<path d="M18.3 5.71c-.39-.39-1.02-.39-1.41 0L12 10.59 7.11 5.7c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>' +
                '</svg>';
            remove.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                void removeItem(index);
            });

            li.appendChild(info);
            li.appendChild(remove);

            if (item.status === 'uploading' || item.status === 'queued') {
                const bar = document.createElement('span');
                bar.className = 'FormMaker__dropzone-file-bar';
                bar.style.width = String(Math.max(2, item.percent)) + '%';
                li.appendChild(bar);
            }

            queue.appendChild(li);
        });
        const pct = overallPercent();
        setZoneProgress(zone, pct, pct !== null && pct < 1);
        syncTokens();
    };

    const startUpload = (item: QueuedFile): void => {
        item.status = 'uploading';
        item.percent = 0;
        render();
        const data = new FormData();
        data.append('action', 'upload');
        data.append('form_id', formId);
        data.append('item_id', itemId);
        data.append('relation_id', relationId);
        data.append('file', item.file, item.file.name);

        const xhr = new XMLHttpRequest();
        item.xhr = xhr;
        xhr.open('POST', uploadUrl);
        xhr.withCredentials = true;
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.upload.onprogress = (event: ProgressEvent): void => {
            if (event.lengthComputable && event.total > 0) {
                item.percent = (event.loaded / event.total) * 100;
            }
            render();
        };
        xhr.onload = (): void => {
            item.xhr = null;
            const payload = parseJsonObject(xhr.responseText);
            const token = payload && typeof payload.token === 'string' ? payload.token : '';
            const ok = payload !== null && payload.ok === true && token !== '';
            if (!ok) {
                item.status = 'error';
                item.percent = 0;
                const err = payload && typeof payload.error === 'string' ? payload.error : '';
                showFeedback(feedback, [uploadErrorMessage(item, xhr.status, err)]);
                render();
                return;
            }
            item.token = token;
            item.status = 'done';
            item.percent = 100;
            render();
        };
        xhr.onerror = (): void => {
            item.xhr = null;
            item.status = 'error';
            item.percent = 0;
            showFeedback(feedback, [zoneI18n(zone, 'upload-failed')]);
            render();
        };
        xhr.send(data);
    };

    const deleteToken = (token: string): void => {
        const data = new FormData();
        data.append('action', 'delete');
        data.append('form_id', formId);
        data.append('item_id', itemId);
        data.append('relation_id', relationId);
        data.append('token', token);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', uploadUrl);
        xhr.withCredentials = true;
        xhr.send(data);
    };

    const reconcileServer = (): Promise<void> => {
        return new Promise((resolve) => {
            const data = new FormData();
            data.append('action', 'reconcile');
            data.append('form_id', formId);
            data.append('item_id', itemId);
            data.append('relation_id', relationId);
            items.forEach((item) => {
                if (item.token) {
                    data.append('keep_tokens[]', item.token);
                }
            });
            const xhr = new XMLHttpRequest();
            xhr.open('POST', uploadUrl);
            xhr.withCredentials = true;
            xhr.onload = (): void => {
                resolve();
            };
            xhr.onerror = (): void => {
                resolve();
            };
            xhr.send(data);
        });
    };

    const uploadErrorMessage = (item: QueuedFile, status: number, err: string): string => {
        if (err === 'max_files' || status === 409) {
            return zoneI18n(zone, 'max-files', { max: String(maxFiles) });
        }
        if (status === 413 || err === 'too_large') {
            return zoneI18n(zone, 'too-large', { files: item.file.name });
        }
        if (err === 'invalid_file') {
            return zoneI18n(zone, 'bad-type', { files: item.file.name });
        }
        return zoneI18n(zone, 'upload-failed');
    };

    const removeItem = async (index: number): Promise<void> => {
        const item = items[index];
        if (!item) {
            return;
        }
        if (item.xhr) {
            item.xhr.abort();
            item.xhr = null;
        }
        if (item.token) {
            deleteToken(item.token);
        }
        items = items.filter((_, i) => i !== index);
        render();
        await reconcileServer();
    };

    const replaceQueue = (files: Array<File>, messages: Array<string>): void => {
        const keepByKey = new Map(items.map((item) => [fileKey(item.file), item]));
        const next: Array<QueuedFile> = [];
        const used = new Set<number>();
        files.forEach((file) => {
            const existing = keepByKey.get(fileKey(file));
            if (existing && !used.has(existing.id)) {
                used.add(existing.id);
                next.push(existing);
                return;
            }
            next.push({
                id: nextQueueId++,
                file,
                token: null,
                status: 'queued',
                percent: 0,
                xhr: null,
                knownSize: file.size,
            });
        });
        items.forEach((item) => {
            if (used.has(item.id)) {
                return;
            }
            if (item.xhr) {
                item.xhr.abort();
            }
            if (item.token) {
                deleteToken(item.token);
            }
        });
        items = next;
        showFeedback(feedback, messages);
        render();
        void (async (): Promise<void> => {
            await reconcileServer();
            items.forEach((item) => {
                if (item.status === 'queued') {
                    startUpload(item);
                }
            });
        })();
    };

    const addIncoming = (incoming: Array<File>): void => {
        const existingFiles = items.map((item) => item.file);
        const result = collectIncoming(existingFiles, incoming, multiple, maxFiles, allowed, maxBytes, zone);
        replaceQueue(result.files, result.messages);
    };

    const clearAll = async (): Promise<void> => {
        items.forEach((item) => {
            if (item.xhr) {
                item.xhr.abort();
                item.xhr = null;
            }
            if (item.token) {
                deleteToken(item.token);
            }
        });
        items = [];
        input.value = '';
        zone.classList.remove('is-dragover');
        showFeedback(feedback, []);
        render();
        await reconcileServer();
    };

    const form = zone.closest('form');
    if (form instanceof HTMLFormElement) {
        form.addEventListener('reset', () => {
            void clearAll();
        });
        if (form.getAttribute('data-formmaker-immediate') !== '1') {
            form.setAttribute('data-formmaker-immediate', '1');
            form.addEventListener('submit', (event: Event) => {
                if (form.getAttribute('data-formmaker-submitting') === '1') {
                    event.preventDefault();
                    return;
                }
                const pending = form.querySelectorAll(
                    '.js-formmaker-dropzone .FormMaker__dropzone-file.is-uploading',
                );
                if (pending.length > 0) {
                    event.preventDefault();
                    const firstZone = form.querySelector('.js-formmaker-dropzone');
                    window.alert(zoneI18n(firstZone, 'uploading'));
                    return;
                }
                const zones = form.querySelectorAll('.js-formmaker-dropzone');
                for (const z of Array.from(zones)) {
                    if (!(z instanceof HTMLElement)) {
                        continue;
                    }
                    const min = Math.max(0, Number(z.getAttribute('data-min-files') || '0'));
                    if (min < 1) {
                        continue;
                    }
                    const tokens = z.querySelectorAll('.js-formmaker-tokens input[type="hidden"]');
                    if (tokens.length < min) {
                        event.preventDefault();
                        showFeedback(ensureFeedback(z), [zoneI18n(z, 'min-files', { min: String(min) })]);
                        z.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                }
                // Zabránit dvojitému odeslání (2. request už nemá captcha/tokeny → falešná chyba + mix zpráv).
                form.setAttribute('data-formmaker-submitting', '1');
                const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
                submitButtons.forEach((btn) => {
                    if (btn instanceof HTMLButtonElement || btn instanceof HTMLInputElement) {
                        btn.disabled = true;
                    }
                });
            });
        }
    }

    bindDropzonePointer(zone, (files) => {
        addIncoming(files);
    });
    input.addEventListener('change', () => {
        const picked = fileListToArray(input.files);
        input.value = '';
        if (picked.length === 0) {
            return;
        }
        addIncoming(picked);
    });

    render();
    void reconcileServer();
}

/** DnD highlight + drop — capture, aby fungovalo i přes skrytý file input / děti. */
function bindDropzonePointer(zone: HTMLElement, onFiles: (files: Array<File>) => void): void {
    let dragDepth = 0;
    const onDragEnter = (event: DragEvent): void => {
        event.preventDefault();
        dragDepth += 1;
        zone.classList.add('is-dragover');
    };
    const onDragOver = (event: DragEvent): void => {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        zone.classList.add('is-dragover');
    };
    const onDragLeave = (): void => {
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) {
            zone.classList.remove('is-dragover');
        }
    };
    const onDrop = (event: DragEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        dragDepth = 0;
        zone.classList.remove('is-dragover');
        const dropped = fileListToArray(event.dataTransfer ? event.dataTransfer.files : null);
        if (dropped.length === 0) {
            return;
        }
        onFiles(dropped);
    };
    zone.addEventListener('dragenter', onDragEnter, true);
    zone.addEventListener('dragover', onDragOver, true);
    zone.addEventListener('dragleave', onDragLeave, true);
    zone.addEventListener('drop', onDrop, true);
}

export function initFormMakerDropzones(root: ParentNode = document): void {
    const zones = root.querySelectorAll('.js-formmaker-dropzone');
    zones.forEach((el) => {
        if (!(el instanceof HTMLElement)) {
            return;
        }
        const zone = el;
        if (zone.getAttribute('data-dropzone-ready') === '1') {
            return;
        }
        const input = zone.querySelector('.js-formmaker-dropzone-input');
        const queue = zone.querySelector('.js-formmaker-dropzone-queue');
        if (!(input instanceof HTMLInputElement) || !(queue instanceof HTMLElement)) {
            return;
        }
        zone.setAttribute('data-dropzone-ready', '1');
        if (zone.getAttribute('data-upload-url')) {
            initImmediateZone(zone, input, queue);
            return;
        }
        const multiple = zone.getAttribute('data-multiple') === '1';
        const maxFiles = Math.max(1, Number(zone.getAttribute('data-max-files') || '1'));
        const maxBytes = Math.max(0, Number(zone.getAttribute('data-max-size-kb') || '0')) * 1024;
        const allowed = parseAllowedExtensions(input.getAttribute('accept') || '');
        const feedback = ensureFeedback(zone);
        let selected: Array<File> = fileListToArray(input.files);
        const sync = (files: Array<File>, messages: Array<string> = []): void => {
            selected = files;
            setInputFiles(input, selected);
            queue.innerHTML = '';
            selected.forEach((file, index) => {
                const li = document.createElement('li');
                li.className = 'FormMaker__dropzone-file';
                li.textContent = file.name;
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.className = 'FormMaker__dropzone-file-remove';
                remove.textContent = '×';
                remove.addEventListener('click', (event) => {
                    event.preventDefault();
                    sync(
                        selected.filter((_, i) => i !== index),
                        [],
                    );
                });
                li.appendChild(remove);
                queue.appendChild(li);
            });
            showFeedback(feedback, messages);
        };
        const addIncomingLocal = (incoming: Array<File>): void => {
            const result = collectIncoming(
                selected,
                incoming,
                multiple,
                maxFiles,
                allowed,
                maxBytes,
                zone,
            );
            sync(result.files, result.messages);
        };
        bindDropzonePointer(zone, addIncomingLocal);
        input.addEventListener('change', () => {
            addIncomingLocal(fileListToArray(input.files));
        });
        const form = zone.closest('form');
        if (form instanceof HTMLFormElement) {
            form.addEventListener('reset', () => {
                input.value = '';
                zone.classList.remove('is-dragover');
                sync([], []);
            });
        }
        sync(selected);
    });
}
